import { Button } from '@ads/components-react';
import { ArrowPathIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/shared/ui/atoms';
import { useStudentGradeDetail } from '../../../hooks/useStudentGradeDetail';
import {
  buildGradeDetailDisciplines,
  fmtFrequency,
  fmtGrade,
  fmtWeight,
  type GradeDetailDisciplineRow,
  type GradeTone,
  gradeTone,
  PASS_FREQUENCY,
  PASS_GRADE,
} from '../../../utils/gradeDetailView';
import { ModuleDot } from '../../atoms/ModuleDot';
import { SectionCard } from '../../molecules/SectionCard';

/** Props do card de detalhe de notas e frequência do aluno. */
export interface StudentGradeDetailCardProps {
  /** Id da matrícula do aluno selecionado. */
  readonly courseRegistrationId: number;
}

/** Classe de cor de texto por tom semântico do valor. */
const TONE_TEXT: Record<GradeTone, string> = {
  ok: 'text-base-success-700',
  bad: 'text-base-danger-700',
  neutral: 'text-ctx-content-placeholder',
};

/** Rótulo + valor colorido (média/frequência) usado no header e por disciplina. */
function StatValue({
  label,
  value,
  tone,
  valueTs,
}: {
  readonly label: string;
  readonly value: string;
  readonly tone: GradeTone;
  /** Classe de tipografia composta (`ts-*`) do valor. */
  readonly valueTs: string;
}) {
  return (
    <span className="flex items-baseline gap-quark">
      <span className="uppercase tracking-wide text-ctx-content-placeholder ts-caption-xxs">
        {label}
      </span>
      <span className={`${valueTs} tabular-nums ${TONE_TEXT[tone]}`}>{value}</span>
    </span>
  );
}

/**
 * Card **Notas e frequência por disciplina** — exibe o detalhe de notas e frequência do
 * aluno selecionado na aba Resumo, consumindo o endpoint `grade-detail`.
 *
 * Mostra a média e a frequência gerais no header e, por disciplina (achatada dos módulos),
 * a média e frequência da disciplina, suas aulas (nota + peso da prova) e a decomposição do
 * cálculo da média ponderada. Resolve internamente carregamento, erro e estado vazio.
 */
export function StudentGradeDetailCard({ courseRegistrationId }: StudentGradeDetailCardProps) {
  const { t } = useTranslation('gradeAttendanceConfig');
  const { detail, isLoading, isError, refetch } = useStudentGradeDetail(courseRegistrationId);

  const disciplines = buildGradeDetailDisciplines(detail);

  return (
    <SectionCard
      title={t('detail.title')}
      icon={<Bars3Icon className="size-xxs text-ctx-content-base" />}
      headerRight={
        detail && !isLoading && !isError ? (
          <div className="flex items-center gap-xxs">
            <StatValue
              label={t('detail.finalGrade')}
              value={fmtGrade(detail.finalGrade, 2)}
              tone={gradeTone(detail.finalGrade, PASS_GRADE)}
              valueTs="ts-heading-xs"
            />
            <StatValue
              label={t('detail.finalFrequency')}
              value={fmtFrequency(detail.finalFrequency)}
              tone={gradeTone(detail.finalFrequency, PASS_FREQUENCY)}
              valueTs="ts-heading-xs"
            />
          </div>
        ) : null
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center px-xxs py-xl">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center gap-micro px-xxs py-xl text-center">
          <p className="text-ctx-content-base ts-paragraph-xs">{t('detail.loadError')}</p>
          <Button
            hierarchy="secondary"
            size="sm"
            leadingIcon={ArrowPathIcon}
            onClick={() => refetch()}
          >
            {t('detail.retry')}
          </Button>
        </div>
      ) : disciplines.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-quark px-xxs py-xl text-center">
          <span className="text-ctx-content-title ts-subtitle-xs">{t('detail.emptyTitle')}</span>
          <p className="max-w-[24rem] text-ctx-content-base ts-paragraph-xs">
            {t('detail.emptyDesc')}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[32rem]">
            {/* Cabeçalho de colunas */}
            <div className="grid grid-cols-[1fr_7.5rem_7.5rem] border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-xxs py-mini uppercase tracking-wide text-ctx-content-placeholder ts-heading-xxxs">
              <span>{t('detail.columns.aula')}</span>
              <span className="text-center">{t('detail.columns.nota')}</span>
              <span className="text-center">{t('detail.columns.peso')}</span>
            </div>

            {disciplines.map(discipline => (
              <DisciplineSection key={discipline.key} discipline={discipline} />
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  );

  /** Bloco de uma disciplina: cabeçalho, aulas e decomposição da média. */
  function DisciplineSection({ discipline }: { readonly discipline: GradeDetailDisciplineRow }) {
    const { formula } = discipline;
    return (
      <div>
        {/* Cabeçalho da disciplina */}
        <div className="flex flex-wrap items-center justify-between gap-xxs border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-xxs py-xxs">
          <span className="flex min-w-none items-center gap-nano">
            <ModuleDot index={discipline.moduleIndex} />
            <span className="text-ctx-content-title ts-subtitle-xxs">{discipline.name}</span>
          </span>
          <span className="flex items-center gap-xxs">
            <StatValue
              label={t('detail.disciplineGrade')}
              value={fmtGrade(discipline.grade, 2)}
              tone={gradeTone(discipline.grade, PASS_GRADE)}
              valueTs="ts-heading-xxs"
            />
            <StatValue
              label={t('detail.disciplineFrequency')}
              value={fmtFrequency(discipline.frequency)}
              tone={gradeTone(discipline.frequency, PASS_FREQUENCY)}
              valueTs="ts-heading-xxs"
            />
          </span>
        </div>

        {/* Aulas da disciplina */}
        {discipline.classes.map(cls => (
          <div
            key={cls.key}
            className="grid grid-cols-[1fr_7.5rem_7.5rem] items-center border-b-hairline border-ctx-layout-border px-xxs py-xxxs"
          >
            <span className="min-w-none truncate text-ctx-content-base ts-paragraph-xs">
              {cls.lessonName}
              {cls.examName ? (
                <span className="text-ctx-content-placeholder">{cls.examName}</span>
              ) : null}
            </span>
            <span
              className={`text-center tabular-nums ts-subtitle-xxs ${TONE_TEXT[gradeTone(cls.grade, PASS_GRADE)]}`}
            >
              {fmtGrade(cls.grade)}
            </span>
            <span className="text-center tabular-nums text-ctx-content-base ts-paragraph-xs">
              {fmtWeight(cls.weight)}
            </span>
          </div>
        ))}

        {/* Decomposição do cálculo da média ponderada */}
        {formula ? (
          <div className="flex flex-col gap-xxxs border-b-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-xxs">
            {/* Legenda dos fatores (um por peso de prova) */}
            <div className="flex flex-wrap gap-nano">
              {formula.buckets.map(bucket => (
                <span
                  key={bucket.symbol}
                  className="inline-flex items-center gap-quark rounded-pill border-hairline border-ctx-layout-border bg-ctx-layout-body px-nano py-quark"
                >
                  <strong className="font-semibold text-xxxs text-ctx-content-title">
                    {bucket.symbol}
                  </strong>
                  <span className="text-ctx-content-base ts-caption-xxs">
                    {t('detail.formula.chipLabel', {
                      count: bucket.count,
                      weight: fmtWeight(bucket.weight),
                    })}
                  </span>
                </span>
              ))}
            </div>

            {/* Fórmula geral (MF) */}
            <div className="flex flex-wrap items-center gap-nano rounded-sm bg-ctx-layout-body px-xxs py-xxxs">
              <span className="text-ctx-content-title ts-subtitle-xxs">
                {t('detail.formula.label')}
              </span>
              <span className="tabular-nums text-ctx-content-base ts-paragraph-xxs">
                {`(${formula.buckets
                  .map(bucket => `(${bucket.symbol} × ${fmtWeight(bucket.weight)})`)
                  .join(' + ')}) ÷ ${fmtWeight(formula.sumWeights)}`}
              </span>
            </div>

            {formula.buckets.map(bucket => (
              <div
                key={bucket.symbol}
                className="flex items-center justify-between gap-xxs rounded-sm bg-ctx-layout-body p-micro"
              >
                <span className="flex items-center gap-quark text-ctx-content-base ts-paragraph-xxs">
                  <strong className="font-semibold text-xxxs text-ctx-content-title">
                    {bucket.symbol}
                  </strong>
                  {t('detail.formula.weightRow', { weight: fmtWeight(bucket.weight) })}
                </span>
                <span className="tabular-nums text-ctx-content-title ts-subtitle-xxs">
                  {`${fmtGrade(bucket.grade)} × ${fmtWeight(bucket.weight)} = ${fmtGrade(bucket.product)}`}
                </span>
              </div>
            ))}

            <div className="mt-quark flex flex-wrap items-center justify-between gap-xxs rounded-sm bg-ctx-content-title p-mini">
              <div className="flex flex-col gap-quark">
                <span className="uppercase tracking-wide text-ctx-layout-surface opacity-medium ts-heading-xxxs">
                  {t('detail.formula.finalLabel')}
                </span>
                <span className="tabular-nums text-ctx-layout-surface ts-paragraph-xxs">
                  {`${fmtGrade(formula.sumProducts)} ÷ ${fmtWeight(formula.sumWeights)} = ${fmtGrade(discipline.grade ?? formula.result, 2)}`}
                </span>
              </div>
              <span className="tabular-nums text-ctx-layout-surface ts-heading-lg">
                {fmtGrade(discipline.grade ?? formula.result, 2)}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}
